"use client";

import React, { useState } from "react";
import ContentDisplayTabs from "../Tabs/ContentDisplayTabs";
import VideoLinksDisplay from "../ContentDisplay/VideoLinksDisplay";
import PerspectiveManagerDisplay from "../ContentDisplay/PerspectiveManagerDisplay";
import ClassificationDisplay from "../ContentDisplay/ClassificationDisplay";
import AlternativeNamesDisplay from "../ContentDisplay/AlternativeNamesDisplay";
import PerspectiveCarousel from "../ContentDisplay/PerspectiveCarousel";
import displayStyles from "../ContentDisplay/ContentDisplay.module.css";
import tabsStyles from "../Tabs/ContentDisplayTabs.module.css";
import Link from "next/link";
import DatabaseImage from "../Media/DatabaseImage";

/**
 * TabbedContentDisplay Component
 *
 * This read-only component displays detailed content in a tabbed interface,
 * replicating the original ContentDisplay page. It includes:
 *
 * - Overview Tab: Shows details, preview navigation (video grid), and the PerspectiveCarousel.
 * - Media Tab: Displays the gallery, perspectives, and video links.
 * - Classification Tab: Shows classification details.
 *
 * The component expects a `content` object with properties such as:
 * status, submission_date, approval_date, created_at, updated_at, description,
 * content_id, public_hash, and gallery_images (an array).
 */
const TabbedContentDisplay = ({ content }) => {
  // Default active tab is Overview
  const [activeTab, setActiveTab] = useState("Overview");

  // Example placeholders for video frames (preview images)
  const previewImages = [null, null, null, null];
  // Use 3 indicators for preview navigation
  const barCount = 3;

  // Placeholder functions for preview navigation (horizontal)
  const handlePrevPreviews = () => {
    // For now, simply a placeholder function for decrementing preview index
    console.log("Prev previews clicked");
  };

  const handleNextPreviews = () => {
    // For now, simply a placeholder function for incrementing preview index
    console.log("Next previews clicked");
  };

  // Placeholder functions for vertical navigation (up/down)
  const handleUpPreviews = () => {
    console.log("Up arrow clicked");
    // Add vertical navigation logic here as needed.
  };

  const handleDownPreviews = () => {
    console.log("Down arrow clicked");
    // Add vertical navigation logic here as needed.
  };

  // Overview tab content
  const overviewTabContent = (
    <div>
      {/* New Details Area at the Top */}
      <div className={displayStyles.details}>
        <span>Status: {content.status || "N/A"}</span>
        <span>
          Submission Date:{" "}
          {content.submission_date
            ? new Date(content.submission_date).toLocaleDateString()
            : "N/A"}
        </span>
        {content.approval_date && (
          <span>
            Approval Date: {new Date(content.approval_date).toLocaleDateString()}
          </span>
        )}
        <span>
          Created At:{" "}
          {content.created_at
            ? new Date(content.created_at).toLocaleDateString()
            : "N/A"}
        </span>
        <span>
          <strong>Updated At:</strong>{" "}
          {content.updated_at
            ? new Date(content.updated_at).toLocaleDateString()
            : "N/A"}
        </span>
      </div>

      {/* Container for Left and Right Columns */}
      <div className={displayStyles.overviewContainer}>
        {/* Left Column: Video Grid and Preview Navigation */}
        <div className={displayStyles.leftColumn}>
          {/* Video Grid for Preview Frames */}
          <div className={displayStyles.videoGrid}>
            {previewImages.map((imageURL, idx) => (
              <div key={`preview-${idx}`} className={displayStyles.videoFrame}>
                {imageURL ? (
                  <DatabaseImage
                    image={imageURL}
                    alt={`Preview ${idx}`}
                    className={displayStyles.videoElement}
                  />
                ) : (
                  <div className={displayStyles.noPreview}></div>
                )}
              </div>
            ))}
          </div>
          {/* Preview Navigation */}
          <div className={displayStyles.previewNavigation}>
            {/* Horizontal Arrow Buttons Container */}
            <div className={displayStyles.arrowContainer}>
              <button className={displayStyles.navButton} onClick={handlePrevPreviews}>
                &#9664;
              </button>
              <button className={displayStyles.navButton} onClick={handleNextPreviews}>
                &#9654;
              </button>
            </div>
            {/* Bars Container (spanning full width) */}
            <div className={displayStyles.barsContainer}>
              {Array.from({ length: barCount }).map((_, idx) => (
                <div key={`bar-${idx}`} className={displayStyles.barFrame} />
              ))}
            </div>
            {/* Vertical Arrow Buttons Container */}
            <div className={displayStyles.verticalArrowContainer}>
              <button className={displayStyles.verticalNavButton} onClick={handleUpPreviews}>
                &#9650;
              </button>
              <button className={displayStyles.verticalNavButton} onClick={handleDownPreviews}>
                &#9660;
              </button>
            </div>
          </div>
        </div>

        {/* Right Column: Perspective Carousel */}
        <div className={displayStyles.rightColumn}>
          <PerspectiveCarousel content={content} />
        </div>
      </div>

      <div className={displayStyles.dmcaLinkContainer}>
        <Link
          href={`/DMCA?contentHash=${content.public_hash}`}
          className={displayStyles.dmcaLink}
        >
          DMCA Request
        </Link>
      </div>

      <p className={displayStyles.description}>
        <strong>Description:</strong> {content.description || "No description available"}
      </p>

      <AlternativeNamesDisplay contentId={content.content_id} />
    </div>
  );

  // Classification Tab: Renders the ClassificationDisplay component.
  const classificationTabContent = <ClassificationDisplay content={content} />;

  // Media Tab: Includes Gallery, Perspectives, and Video Links.
  const mediaTabContent = (
    <div>
      <h1>Gallery</h1>
      {Array.isArray(content.gallery_images) && content.gallery_images.length > 0 ? (
        <div className={tabsStyles.galleryContainer}>
          {content.gallery_images.map((image, index) => (
            <DatabaseImage
              key={index}
              image={image}
              alt={`Gallery Image ${index + 1}`}
              className={tabsStyles.galleryImage}
            />
          ))}
        </div>
      ) : (
        <p>No content available yet.</p>
      )}
      <h1>Perspectives</h1>
      <PerspectiveManagerDisplay content={content} />
      <h1>Video Links</h1>
      <VideoLinksDisplay content={content} />
    </div>
  );

  // Return the ContentDisplayTabs component with Overview, Media, and Classification tabs.
  return (
    <ContentDisplayTabs
      tabs={[
        { label: "Overview", content: overviewTabContent },
        { label: "Media", content: mediaTabContent },
        { label: "Classification", content: classificationTabContent },
      ]}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
    />
  );
};

export default TabbedContentDisplay;
